import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { logout, setCredentials, setSessionExpired, setOrganizationSuspended, setStoreInactive } from '../store/authSlice';

const baseQuery = fetchBaseQuery({
  baseUrl: process.env.REACT_APP_API_BASE_URL,
  prepareHeaders: (headers, { getState }) => {
    const token = getState().auth.token;
    if (token) {
      headers.set('authorization', `Bearer ${token}`);
    }
    return headers;
  },
});

// A suspended organization returns 403 with a distinct `organization_suspended`
// error key — on any request, login, or token refresh. Detect the key (not just
// the status) and flip global state so the app routes to the suspended screen.
const isOrganizationSuspended = (result) =>
  result?.error?.status === 403 && result.error.data?.error === 'organization_suspended';

// A deactivated store returns 403 "This store is inactive" on every POS request,
// and "This store is inactive. Contact your admin." on login/terminal-select —
// match the shared prefix so the app blocks POS entry for that store.
const isStoreInactive = (result) =>
  result?.error?.status === 403 &&
  typeof result.error.data?.error === 'string' &&
  result.error.data.error.startsWith('This store is inactive');

export const baseQueryWithReauth = async (args, api, extraOptions) => {
  let result = await baseQuery(args, api, extraOptions);

  if (isOrganizationSuspended(result)) {
    api.dispatch(setOrganizationSuspended());
    return result;
  }

  if (isStoreInactive(result)) {
    api.dispatch(setStoreInactive());
    return result;
  }

  if (result.error && result.error.status === 401) {
    const refreshToken = api.getState().auth.refreshToken;

    if (refreshToken) {
      const refreshResult = await baseQuery(
        {
          url: '/auth/refresh',
          method: 'POST',
          body: { auth: { refresh_token: refreshToken } },
        },
        api,
        extraOptions
      );

      if (refreshResult.data) {
        api.dispatch(setCredentials({
          access_token: refreshResult.data.access_token,
          refresh_token: refreshResult.data.refresh_token,
        }));
        result = await baseQuery(args, api, extraOptions);
      } else if (isOrganizationSuspended(refreshResult)) {
        // Org suspended mid-session — show the suspended screen, not a generic expiry.
        api.dispatch(setOrganizationSuspended());
      } else if (isStoreInactive(refreshResult)) {
        // Store taken offline mid-session — block POS entry, not a generic expiry.
        api.dispatch(setStoreInactive());
      } else {
        // Refresh failed — session was revoked (e.g. manager unblocked terminal).
        // Only reaches here when user was already logged in and refresh token existed.
        api.dispatch(setSessionExpired());
      }
    }
    // No else — if there's no refreshToken the user isn't logged in yet.
    // Let the 401 bubble back to the caller (e.g. login page shows its own error toast).
  }
  return result;
};

export const authApi = createApi({
  reducerPath: 'authApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['User'],
  endpoints: (builder) => ({

    // Step 1 — credential check, no JWT issued yet
    // Returns: { data: { user, store, terminals[] } }
    login: builder.mutation({
      query: (credentials) => ({
        url: '/auth/login',
        method: 'POST',
        body: { auth: credentials },
      }),
    }),

    // Step 2 — terminal selection, issues JWT
    // Returns: { data: { user, store, organization, terminal, access_token, refresh_token } }
    selectTerminal: builder.mutation({
      query: (credentials) => ({
        url: '/auth/select_terminal',
        method: 'POST',
        body: { auth: credentials },
      }),
    }),

    getMe: builder.query({
      query: () => '/auth/me',
      providesTags: ['User'],
    }),

    logoutApi: builder.mutation({
      query: () => ({
        url: '/auth/logout',
        method: 'DELETE',
      }),
    }),
  }),
});

export const {
  useLoginMutation,
  useSelectTerminalMutation,
  useGetMeQuery,
  useLazyGetMeQuery,
  useLogoutApiMutation,
} = authApi;
